import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridPaginationModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridOverlay,
  GridToolbarQuickFilter,
  GridValueGetter,
} from "@mui/x-data-grid";
import {
  Button,
  Typography,
  Box,
  Grid,
  styled,
  CircularProgress,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import ToastSnackbar, { SnackbarHandle } from "../../common/ToastNotification";
import HomeIcon from "@mui/icons-material/Home";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useApi } from "../../store/UserLoginContext";
import { useForm } from "react-hook-form";
import ControlledSelect from "../../common/ControlledComponents/ControlledSelect";
import { Get as GetClass } from "../../api/Control-Settings/manage-class";
import { GetSections } from "../../api/Exams-Management/manage-section";
import { useReactToPrint } from "react-to-print";
import BonafideReportTemplate from "../../templates/BonafideReportTemplate";
import { ListStudentsForBonafideReport } from "../../api/Report-Center/bonafide-report";
import BonafideReportDialog from "./BonafideReportDialog";

const CustomNoRowsOverlay = ({ loading }: { loading: boolean }) => {
  return (
    <GridOverlay
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <Box sx={{ textAlign: "center", padding: 2 }}>
        {loading ? (
          <>
            <CircularProgress size={40} />
            <Typography variant="h6" color="textSecondary" mt={2}>
              Loading data...
            </Typography>
          </>
        ) : (
          <Typography variant="h5" color="textSecondary">
            NO DATA AVAILABLE
          </Typography>
        )}
      </Box>
    </GridOverlay>
  );
};

const AnimatedButton = ({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <Button
      variant="text"
      onClick={onClick}
      sx={{
        position: "relative",
        // fontFamily: "Motiva Sans Bold",
        // fontWeight: "Bold",
        padding: "0 10px",
        fontSize: "14px",
        textTransform: "none",
        color: "#2e186a",
        "&:hover": {
          fontWeight: "Bold",
        },
        "&::after": {
          content: '""',
          fontWeight: "Bold",
          position: "absolute",
          width: "0",
          height: "2px",
          left: "0",
          bottom: "-2px",
          backgroundColor: "rgb(255, 130, 92)",
          transition: "width 0.3s ease-in-out",
        },
        "&:hover::after": {
          width: "100%", // Underline expands on hover
        },
      }}
      disabled={disabled == null ? true : disabled}
    >
      <strong>{label}</strong>
    </Button>
  );
};

const MyCustomButton = styled(Button)(({ theme }) => ({
  fontFamily: "Motiva Sans Bold",
  fontSize: "0.80rem",
  fontWeight: "700",
  border: "1px solid #edf3f5",
  borderRadius: "4px",
  background: "#2e186a",
  boxShadow: "0 16px 30px rgb(23 31 114 / 20%)",
  marginTop: "0rem",
  "&:hover": {
    color: "#fff",
    border: "1px solid rgb(255, 130, 92)",
    backgroundColor: "rgb(255, 130, 92)",
  },
}));

const BonafideReport = () => {
  const { currentSessionAY } = useApi();
  const snackbarRef = React.useRef<SnackbarHandle>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sectionList, setSectionList] = useState<any>([]);
  const [classRecords, setClassRecords] = useState<any>([]);
  const [adding, setAdding] = useState(false);
  const [printData, setPrintData] = useState<any>([]);
  const [readyToPrint, setReadyToPrint] = useState(false);
  const containerRef = useRef();
  const [applications, setApplications] = useState<any>([]);
  const [fetching, setFetching] = useState(false);
  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [isOpenBonafideReportDialog, setIsOpenBonafideReportDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classes_records, section_records] = await Promise.all([
          GetClass(),
          GetSections(),
        ]);

        if (classes_records && classes_records.result.documents?.length > 0) {
          setClassRecords(classes_records.result.documents);
        }
        if (section_records && section_records.result.documents?.length > 0) {
          setSectionList(section_records.result.documents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        snackbarRef.current?.showSnackbar(`Error fetching data`, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
    setError,
    reset,
  } = useForm({
    defaultValues: {
      class_id: "",
      section_id: "",
    },
    mode: "onTouched",
  });

  const handlePrint = useReactToPrint({
    documentTitle: "",
    onAfterPrint: () => setReadyToPrint(false),
  }) as (content: () => HTMLElement | null) => () => void;

  const handleFetchStudents = async (data : any) => {
    // console.log(data);
    try {
      setFetching(true);
      setApplications([]);
      const { class_id, section_id } = data;

      if (!class_id || !section_id) {
        
        setError("class_id", {
          type: "manual",
          message: "Class and Section are required",
        });

        setError("section_id", {
          type: "manual",
          message: "Class and Section are required",
        });

        return;
      }

      const payload = {
        class_id,
        section_id,
        academic_year: currentSessionAY,
      };
      console.log(payload);

      const students_records = await ListStudentsForBonafideReport(payload);
      if (students_records && students_records.result.length > 0) {
        console.log(students_records);
        setApplications(students_records.result.sort((a: any, b: any) => a.roll_number - b.roll_number));

      } else {
        snackbarRef.current?.showSnackbar(
          `No students found for the selected class and section`,
          "info"
        );
      }


    } catch (error) {
      console.error("Error fetching students:", error);
      snackbarRef.current?.showSnackbar(
        `Error fetching students`,
        "error"
      );
    } finally {
      setFetching(false);
    }
  }

  const onResetHandler = () => {
    reset({
      class_id: "",
      section_id: "",
    });
  };

  const handleViewClick = useCallback(async (rowData: any) => {
    console.log("View clicked for row:", rowData);

    setPrintData(rowData);
    // setReadyToPrint(true);

    // setTimeout(() => {
    //   try {
    //     handlePrint(() => containerRef.current!)();
    //   } catch (error) {
    //     console.log("Handling print function call error.");
    //     console.log(error);
    //   }
    // }, 400);

    setIsOpenBonafideReportDialog(true);

  }, []);

  const handleCloseBonafideReportDialog = () => {
    setIsOpenBonafideReportDialog(false);
  }

  const columns: GridColDef[] = [
    {
      field: "admission_no",
      headerName: "Admission No",
      flex: 1,
      valueGetter: (_, row) => {
        return row.admission_id.toString().padStart(5, "0");
      },
    },
    {
      field: "personal_details",
      headerName: "Name",
      flex: 1,
      valueGetter: (_, row) => row.name,
    },
    {
      field: "parent_details",
      headerName: "Father's Name",
      flex: 1,
      valueGetter: (_, row) => row.father_name,
    },
    {
      field: "roll_number",
      headerName: "Roll No.",
      flex: 0.5,
      valueGetter: (_, row) => row.roll_number,
      // valueGetter: (_, row) => row.academic_records[0]?.roll_number,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 2,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <AnimatedButton
            label="Report"
            onClick={() => {
              handleViewClick(params.row);
            }}
            disabled={false}
          />
        </>
      )
    }
  ]

  return (
    <>
      <ToastSnackbar ref={snackbarRef} />
      {readyToPrint && (
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: "0",
          }}
        >
          {/* @ts-ignore */}
          <BonafideReportTemplate ref={containerRef} data={printData} />
        </div>
      )}
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        p={2}
        pb={0}
        gap={1}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-start"}
          p={2}
          pb={0}
          gap={1}
        >
          <MyCustomButton
            variant="contained"
            startIcon={<ArrowLeftIcon />}
            onClick={() => navigate("..")}
          >
            Go Back
          </MyCustomButton>
          <MyCustomButton
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/home")}
          >
            Home
          </MyCustomButton>
        </Box>
      </Box>

      <Box
        display={"flex"}
        flexDirection={"column"}
        p={2}
        pt={0}
        height="auto"
        width="auto"
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          p={2}
          pt={0}
          height="auto"
          justifyContent={"center"}
          alignItems={"center"}
          width={"90vw"}
        >
          <Typography variant="h6" alignSelf={"center"}>
            <strong>Select Class And Section To Fetch Bonafide Reports</strong>
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-evenly"}
            gap={3}
            width="auto"
            mt={4}
          >
            {loading ? (
              <Box>
                <CircularProgress />
              </Box>
            ) : (
              <Grid xs={12}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <form
                    onSubmit={handleSubmit(handleFetchStudents)}
                    onReset={onResetHandler}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent={"space-evenly"}
                      gap={3}
                      width="90vw"
                    >
                      <ControlledSelect
                        name={`class_id`}
                        control={control}
                        errors={errors}
                        label="Class"
                        options={classRecords.map((item: any) => ({
                          value: item.class_id,
                          label: item.name,
                        }))}
                        sx={{ width: "30%" }}
                        // selectProps={{ onChange: HandleClassChange }}
                      />
                      <ControlledSelect
                        name={`section_id`}
                        control={control}
                        errors={errors}
                        label="Section"
                        options={sectionList.map((item: any) => ({
                          value: item.section_id,
                          label: item.name,
                        }))}
                        sx={{ width: "30%" }}
                        // selectProps={{ onChange: HandleClassChange }}
                      />
                      <MyCustomButton
                        variant="contained"
                        type="submit"
                        startIcon={fetching ? <CircularProgress size={20} /> : null}
                        disabled={fetching}
                        sx={{
                          alignSelf: "center",
                          height: "70%",
                          width: "20%",
                        }}
                      >
                        {fetching ? "" : "Fetch Report"}
                      </MyCustomButton>
                      <MyCustomButton
                        variant="contained"
                        type="reset"
                        sx={{
                          alignSelf: "center",
                          height: "70%",
                          width: "20%",
                        }}
                      >
                        Clear
                      </MyCustomButton>
                    </Box>
                  </form>
                </Box>
                <DataGrid
                  rows={applications}
                  columns={columns}
                  rowHeight={40}
                  getRowId={(row) => row.student_id}
                  pageSizeOptions={[50, 100]}
                  loading={fetching}
                  // autoHeight // Prevents unnecessary re-renders
                  rowBufferPx={300} // Loads extra rows for smoother scrolling
                  disableColumnFilter // Improves performance
                  disableColumnMenu // Removes menu for better speed
                  disableRowSelectionOnClick // Prevents unnecessary re-renders
                  // loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel} // Controls pagination behavior
                  checkboxSelection={false}
                  slots={{
                    toolbar: GridToolbar,
                    noRowsOverlay: () => <CustomNoRowsOverlay loading={fetching} />,
                  }}
                  slotProps={{ toolbar: { showQuickFilter: true } }}
                  //   slots={{
                  //     // toolbar: GridToolbar, // Enables search functionality and other features
                  //     toolbar: CustomToolbar,
                  //   }}
                  sx={{
                    maxWidth: "98vw",
                    height: "65vh", // Fixed height for the grid
                    // Target the column headers specifically

                    "& .MuiDataGrid-row:hover": {
                      transform: "scale(1)", // Hover zoom effect
                      backgroundColor: "#f5f5f5", // Light background on hover
                      "& .MuiDataGrid-cell": {
                        color: "#2E186A", // Change text color on hover
                        fontWeight: "bold", // Bold text on hover
                      },
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "#f0f0f0", // Active row background color
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#1e88e5", // Custom background color for header
                      // fontFamily: "Motiva Sans Bold",
                      color: "#2e186a", // Custom text color for header
                      fontSize: "1rem", // Increase font size of header
                      borderBottom: "2px solid #fff", // Add a border at the bottom of headers
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textOverflow: "clip", // Prevent ellipsis from appearing
                      whiteSpace: "normal", // Ensure text wraps if it's too long
                      lineHeight: "1", // Adjust line height
                    },
                    "& .MuiDataGrid-columnHeader": {
                      padding: "0px 10px", // Add padding to header cells
                    },
                  }}
                />
              </Grid>
            )}
          </Box>
        </Box>
      </Box>
      {isOpenBonafideReportDialog && (
        <BonafideReportDialog
          isOpen={isOpenBonafideReportDialog}
          onClose={handleCloseBonafideReportDialog}
          printData={printData}
        />
      )

      }
    </>
  );
};

export default BonafideReport;